/**
 * Git Integration List
 * 展示所有配置的 Git 仓库集成
 */

import React from 'react';
import { Table, TableColumn } from '@backstage/core-components';
import {
  Button, Chip, Box, Typography, CircularProgress,
  IconButton, Tooltip,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { GitIntegrationListItem } from './types';

interface GitIntegrationListProps {
  integrations: GitIntegrationListItem[];
  loading: boolean;
  onRefresh: () => void;
  onCheck: (host: string) => void;
  onViewDetail: (host: string) => void;
  checkingHost?: string;
  checkResults?: Map<string, { status: 'ok' | 'warning' | 'error'; checkedAt?: string }>;
}

export const GitIntegrationList: React.FC<GitIntegrationListProps> = ({
  integrations,
  loading,
  onRefresh,
  onCheck,
  onViewDetail,
  checkingHost,
  checkResults,
}) => {
  const getCheckInfo = (host: string) => {
    if (checkingHost === host) {
      return { status: 'checking' as const };
    }
    const result = checkResults?.get(host);
    if (!result) {
      return { status: 'notChecked' as const };
    }
    return { status: result.status, checkedAt: result.checkedAt };
  };

  const columns: TableColumn<GitIntegrationListItem>[] = [
    {
      title: '类型',
      field: 'type',
      width: '80px',
      render: (row) => (
        <Chip
          label={row.type === 'gitlab' ? 'GitLab' : row.type}
          size="small"
          style={{
            background: row.type === 'gitlab' ? '#FC6D26' : '#333',
            color: '#fff',
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      title: 'Host',
      field: 'host',
      render: (row) => (
        <Box>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {row.host}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {row.apiBaseUrl}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Token',
      field: 'tokenMasked',
      render: (row) => (
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          <Chip
            label={row.tokenMasked}
            size="small"
            variant="outlined"
            style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
          />
          {row.hasToken ? (
            <CheckCircleIcon style={{ fontSize: 14, color: '#4caf50' }} />
          ) : (
            <ErrorIcon style={{ fontSize: 14, color: '#f44336' }} />
          )}
        </Box>
      ),
    },
    {
      title: '状态',
      render: (row) => {
        const info = getCheckInfo(row.host);

        if (info.status === 'checking') {
          return (
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="textSecondary">检测中...</Typography>
            </Box>
          );
        }

        if (info.status === 'ok') {
          return (
            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
              <CheckCircleIcon style={{ fontSize: 16, color: '#4caf50' }} />
              <Typography variant="caption" style={{ color: '#4caf50', fontWeight: 500 }}>正常</Typography>
              {info.checkedAt && (
                <Typography variant="caption" color="textSecondary" style={{ marginLeft: 4 }}>
                  {new Date(info.checkedAt).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </Typography>
              )}
            </Box>
          );
        }

        if (info.status === 'warning') {
          return (
            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
              <WarningIcon style={{ fontSize: 16, color: '#ff9800' }} />
              <Typography variant="caption" style={{ color: '#ff9800', fontWeight: 500 }}>即将过期</Typography>
              {info.checkedAt && (
                <Typography variant="caption" color="textSecondary" style={{ marginLeft: 4 }}>
                  {new Date(info.checkedAt).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </Typography>
              )}
            </Box>
          );
        }

        if (info.status === 'error') {
          return (
            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
              <ErrorIcon style={{ fontSize: 16, color: '#f44336' }} />
              <Typography variant="caption" style={{ color: '#f44336', fontWeight: 500 }}>异常</Typography>
            </Box>
          );
        }

        return (
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <RemoveCircleOutlineIcon style={{ fontSize: 16, color: '#bbb' }} />
            <Typography variant="caption" color="textSecondary">未检测</Typography>
          </Box>
        );
      },
    },
    {
      title: '操作',
      render: (row) => {
        const info = getCheckInfo(row.host);
        const isChecking = info.status === 'checking';
        const hasResult = info.status === 'ok' || info.status === 'warning' || info.status === 'error';

        return (
          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
            <Button
              size="small"
              variant={hasResult ? 'outlined' : 'contained'}
              color="primary"
              startIcon={<PlayArrowIcon style={{ fontSize: 16 }} />}
              onClick={() => onCheck(row.host)}
              disabled={isChecking}
              style={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto', padding: '2px 10px' }}
            >
              {hasResult ? '重新检测' : '检测'}
            </Button>
            {hasResult && (
              <Button
                size="small"
                variant="text"
                color="primary"
                startIcon={<VisibilityIcon style={{ fontSize: 16 }} />}
                onClick={() => onViewDetail(row.host)}
                style={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto', padding: '2px 10px' }}
              >
                详情
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Tooltip title="刷新">
          <IconButton size="small" onClick={onRefresh}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Table
        options={{
          search: false,
          paging: false,
          padding: 'dense',
          toolbar: false,
        }}
        columns={columns}
        data={integrations}
        isLoading={loading}
        emptyContent={
          <Box p={2} textAlign="center">
            <Typography color="textSecondary">
              暂无配置的 Git 集成，请在 app-config.yaml 中配置 integrations.gitlab
            </Typography>
          </Box>
        }
      />
    </Box>
  );
};
